import { Line, Bar } from 'react-chartjs-2';
import React, { useEffect } from 'react';
import { WaterMonitoringChartItem } from '../../../models/types/waterMonitoring';
import dayjs from 'dayjs';
import { WaterMonitoringChartRequest } from '../../../models/types/waterMonitoring';

interface RenderChartProps {
    waterMonitoring: WaterMonitoringChartItem[];
    chartType: 'line' | 'bar';
    visibleParams: string[];
    setVisibleParams: React.Dispatch<React.SetStateAction<string[]>>;
    t: (key: string) => string;
    timeRange: WaterMonitoringChartRequest;
}

const RenderChart: React.FC<RenderChartProps> = ({
    waterMonitoring,
    chartType,
    visibleParams,
    setVisibleParams,
    t,
    timeRange
}) => {
    // Lấy tất cả các chỉ số duy nhất (để render legend/checkbox)
    const allParams = waterMonitoring && Array.isArray(waterMonitoring)
        ? Array.from(new Set(waterMonitoring.map(item => item.parameterName)))
        : [];
    // Hàm bật/tắt chỉ số trên biểu đồ
    const handleToggleParam = (paramName: string) => {
        setVisibleParams(prev =>
            prev.includes(paramName)
                ? prev.filter(k => k !== paramName)
                : [...prev, paramName]
        );
    };

    // Khi lần đầu render, nếu visibleParams rỗng thì setVisibleParams = [allParams[0]]
    useEffect(() => {
        if (visibleParams.length === 0 && allParams.length > 0) {
            setVisibleParams([allParams[0]]);
        }
    }, [visibleParams, allParams, setVisibleParams]);

    // Chuẩn hóa dữ liệu cho biểu đồ chỉ với các chỉ số đang hiển thị
    const chartData = (() => {
        if (!waterMonitoring || !Array.isArray(waterMonitoring) || waterMonitoring.length === 0) return null;
        // Lấy tất cả các ngày (timestamp) duy nhất, đã sort tăng dần
        const allDates = Array.from(new Set(waterMonitoring.map(item => item.timestamp))).sort();
        // Tạo dataset cho từng parameterName (chỉ lấy những cái đang hiển thị)
        const datasets = allParams.filter(paramName => visibleParams.includes(paramName)).map((paramName, idx) => {
            // Màu sắc cho từng line
            const colors = [
                '#3366CC', '#DC3912', '#FF9900', '#109618', '#990099',
                '#3B3EAC', '#0099C6', '#DD4477', '#66AA00', '#B82E2E',
                '#316395', '#994499', '#22AA99', '#AAAA11', '#6633CC',
                '#E67300', '#8B0707', '#329262', '#5574A6', '#3B3EAC'
            ];
            const unit = waterMonitoring.find(item => item.parameterName === paramName)?.unit;
            return {
                label: `${paramName} (${unit})`,
                data: allDates.map(date => {
                    const found = waterMonitoring.find(item => item.timestamp === date && item.parameterName === paramName);
                    return found ? found.avgValue : null;
                }),
                borderColor: colors[idx % colors.length],
                backgroundColor: chartType === 'bar' ? colors[idx % colors.length] + '88' : colors[idx % colors.length],
                spanGaps: true,
                fill: false,
                tension: 0.4,
            };
        });
        return {
            labels: allDates.map(date => dayjs(date).format(timeRange.type === 'hour' ? 'DD-MM-YYYY HH:mm' : 'DD-MM-YYYY')),
            datasets,
        };
    })();

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top' as const,
            },
            title: {
                display: true,
                text: t('Biểu đồ các chỉ số nước ' + (timeRange.type === 'hour' ? 'theo giờ' : 'theo ngày')),
            },
            tooltip: {
                mode: 'index' as const,
                intersect: false,
            },
        },
        interaction: {
            mode: 'nearest' as const,
            axis: 'x' as const,
            intersect: false,
        },
        scales: {
            y: {
                beginAtZero: true,
            },
        },
    };

    return (
        <div className="bg-white md:p-4 rounded-lg">
            <div>
                {/* Legend thủ công cho phép bật/tắt chỉ số */}
                <div className="flex flex-wrap gap-2 my-2">
                    {allParams.length === 0 ? (
                        <span className="text-gray-400 text-xs">Chưa có dữ liệu chỉ số</span>
                    ) : (
                        allParams.map(param => (
                            <button
                                key={param}
                                className={`px-2 py-1 rounded text-xs border ${visibleParams.includes(param) ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
                                onClick={() => handleToggleParam(param)}
                            >
                                {param}
                            </button>
                        ))
                    )}
                </div>
                {chartData && (
                    chartType === 'line' ? (
                        <div className="h-[500px] w-full">
                            <Line data={chartData} options={chartOptions} />
                        </div>
                    ) : (
                        <div className="h-[500px] w-full">
                            <Bar data={chartData} options={chartOptions} />
                        </div>
                    )
                )}
            </div>
        </div>
    );
};

export default RenderChart;